import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Truck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg, fmtQty, todayIST } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Field, Empty } from "@/components/common";
import { AttachmentPicker } from "@/pages/InwardNew";
import { SignaturePad } from "@/components/SignaturePad";
import { ScanButton } from "@/components/QrScanner";

const uuid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

function ItemPicker({ bill, chosen, onAdd }) {
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  useEffect(() => { const t = setTimeout(() => api.get("/dispatchable", { params: { q, bill_no: bill } }).then((r) => setList(r.data)), 250); return () => clearTimeout(t); }, [q, bill]);
  const ids = new Set(chosen.map((c) => c.unit.id));
  return (
    <div className="space-y-2">
      <Input placeholder="Search cut piece, drum, bill, size…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="dispatch-item-search-input" />
      <div className="max-h-64 overflow-y-auto rounded-md border divide-y" data-testid="dispatch-item-options">
        {list.filter((u) => !ids.has(u.id)).map((u) => (
          <div key={u.id} className="flex items-center gap-3 px-3 py-2 text-sm">
            <span className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded ${u.kind === "cut_piece" ? "bg-red-600/10 text-red-600" : "bg-muted"}`}>{u.kind.replace("_", " ")}</span>
            <span className="font-mono">{u.drum_code}</span>
            <span className="flex-1 truncate text-muted-foreground">{u.brand} · {u.cable_size}{u.bill_no ? ` · Bill ${u.bill_no}` : ""}{u.party_name ? ` · ${u.party_name}` : ""}</span>
            <span className="font-mono">{fmtQty(u.available, u.uom)}</span>
            <Button size="sm" variant="outline" onClick={() => onAdd(u)} data-testid={`dispatch-add-${u.drum_code}`}><Plus className="h-3 w-3" /></Button>
          </div>
        ))}
        {!list.length && <Empty text="Nothing available to dispatch" />}
      </div>
    </div>
  );
}

export default function DispatchNew() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [h, setH] = useState({ date: todayIST(), party_name: "", bill_no: "", vehicle_no: "", driver_name: "", driver_mobile: "", transporter: "", lr_no: "", delivery_address: "", taken_by: "", issued_by: user?.name || "", verified_by: "", remarks: "", recipient_name: "", recipient_mobile: "" });
  const [items, setItems] = useState([]);
  const [files, setFiles] = useState([]);
  const [sig, setSig] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [ref] = useState(uuid());
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/customers").then((r) => setCustomers(r.data)); }, []);
  const s = (k) => (e) => setH({ ...h, [k]: e.target.value });
  const add = (u) => {
    setItems([...items, { unit: u, qty: String(u.available) }]);
    if (!h.bill_no && u.bill_no && u.bill_no !== "PENDING") setH((p) => ({ ...p, bill_no: u.bill_no, party_name: p.party_name || u.party_name || "" }));
  };
  const submit = async () => {
    setBusy(true);
    try {
      const cust = customers.find((c) => c.name.toLowerCase() === h.party_name.trim().toLowerCase());
      const { data } = await api.post("/dispatches", { ...h, client_ref: ref, party_id: cust?.id || null, attachment_ids: files.map((f) => f.id),
        signature_attachment_id: sig?.id || null, items: items.map((i) => ({ unit_id: i.unit.id, qty: +i.qty })) });
      toast.success(`${data.dispatch_no} dispatched — stock deducted`);
      nav(`/dispatch/${data.id}`);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="space-y-6 pb-24">
      <PageHeader title="New Dispatch" subtitle="Add cut pieces (dispatched in full) or quantities from drums. Stock is deducted once, on submit." />
      <section className="rounded-lg border bg-card p-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Field label="Dispatch date" required><Input type="date" value={h.date} max={todayIST()} onChange={s("date")} data-testid="dispatch-date-input" /></Field>
        <Field label="Party name" required><Input list="dsp-party" value={h.party_name} onChange={s("party_name")} data-testid="dispatch-party-input" /><datalist id="dsp-party">{customers.map((c) => <option key={c.id} value={c.name} />)}</datalist></Field>
        <Field label="Bill number" required><Input value={h.bill_no} onChange={(e) => setH({ ...h, bill_no: e.target.value.toUpperCase() })} data-testid="dispatch-bill-input" /></Field>
        <Field label="Vehicle number"><Input value={h.vehicle_no} onChange={(e) => setH({ ...h, vehicle_no: e.target.value.toUpperCase() })} data-testid="dispatch-vehicle-input" /></Field>
        <Field label="Driver name"><Input value={h.driver_name} onChange={s("driver_name")} data-testid="dispatch-driver-input" /></Field>
        <Field label="Driver mobile"><Input type="tel" value={h.driver_mobile} onChange={s("driver_mobile")} data-testid="dispatch-driver-mobile-input" /></Field>
        <Field label="Transporter"><Input value={h.transporter} onChange={s("transporter")} data-testid="dispatch-transporter-input" /></Field>
        <Field label="LR number"><Input value={h.lr_no} onChange={s("lr_no")} data-testid="dispatch-lr-input" /></Field>
        <Field label="Material taken by"><Input value={h.taken_by} onChange={s("taken_by")} data-testid="dispatch-taken-by-input" /></Field>
        <Field label="Material issued by"><Input value={h.issued_by} onChange={s("issued_by")} data-testid="dispatch-issued-by-input" /></Field>
        <Field label="Verified by"><Input value={h.verified_by} onChange={s("verified_by")} data-testid="dispatch-verified-by-input" /></Field>
        <Field label="Remarks"><Input value={h.remarks} onChange={s("remarks")} data-testid="dispatch-remarks-input" /></Field>
        <Field label="Delivery address (defaults to customer master)" className="col-span-2 lg:col-span-4"><Textarea rows={2} value={h.delivery_address} onChange={s("delivery_address")} data-testid="dispatch-address-input" /></Field>
      </section>
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4 [&>*]:min-w-0">
        <div className="rounded-lg border bg-card p-4 space-y-2">
          <div className="flex items-center justify-between gap-2"><div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">Available to dispatch</div>
            <ScanButton testid="dispatch-scan-btn" onDrum={(u) => { if (items.some((x) => x.unit.id === u.id)) { toast.message("Already added"); return; } if (u.available <= 0) { toast.error(`${u.drum_code} has no free balance`); return; } add(u); }} /></div>
          <ItemPicker bill={h.bill_no} chosen={items} onAdd={add} />
        </div>
        <div className="rounded-lg border bg-card p-4 space-y-2" data-testid="dispatch-items">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">Items in this dispatch ({items.length})</div>
          {items.map((i, idx) => (
            <div key={i.unit.id} className="flex items-center gap-2 text-sm border rounded-md px-3 py-2" data-testid={`dispatch-item-${i.unit.drum_code}`}>
              <span className="font-mono">{i.unit.drum_code}</span><span className="flex-1 truncate text-muted-foreground">{i.unit.brand} · {i.unit.cable_size}</span>
              <Input type="number" step="0.001" className="w-28 h-8 font-mono" value={i.qty} disabled={i.unit.kind === "cut_piece"}
                onChange={(e) => setItems(items.map((x, j) => (j === idx ? { ...x, qty: e.target.value } : x)))} data-testid={`dispatch-item-qty-${i.unit.drum_code}`} />
              <span className="text-xs w-6">{i.unit.uom}</span>
              <Button size="icon" variant="ghost" onClick={() => setItems(items.filter((_, j) => j !== idx))} data-testid={`dispatch-item-remove-${i.unit.drum_code}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
          {!items.length && <Empty text="Add items from the left" />}
        </div>
      </section>
      <section className="rounded-lg border bg-card p-4 grid lg:grid-cols-2 gap-4">
        <div className="space-y-3">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">Material handover (optional now, can be done later)</div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Recipient name"><Input value={h.recipient_name} onChange={s("recipient_name")} data-testid="dispatch-recipient-name-input" /></Field>
            <Field label="Recipient mobile"><Input type="tel" value={h.recipient_mobile} onChange={s("recipient_mobile")} data-testid="dispatch-recipient-mobile-input" /></Field>
          </div>
          <SignaturePad onSaved={setSig} testid="dispatch-signature" />
        </div>
        <Field label="Signed copy / bill attachments"><AttachmentPicker files={files} setFiles={setFiles} testid="dispatch-attach" /></Field>
      </section>
      <div className="fixed bottom-16 lg:bottom-0 left-0 right-0 lg:left-64 z-30 border-t bg-background/95 backdrop-blur px-4 sm:px-8 py-3 flex justify-end gap-3">
        <Button variant="outline" onClick={() => nav("/dispatch")}>Cancel</Button>
        <Button className="bg-red-600 hover:bg-red-700 text-white min-w-44" disabled={busy || !items.length || !h.bill_no || !h.party_name} onClick={submit} data-testid="dispatch-submit-btn"><Truck className="h-4 w-4 mr-2" />{busy ? "Dispatching…" : "Confirm dispatch"}</Button>
      </div>
    </div>
  );
}
